import React, { useState, useEffect } from "react";
import axios from "axios";
import { Nav, SpineRail, Footer } from "../components/site/Chrome";
import { Reveal, Eyebrow } from "../components/site/primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Camera, Clock, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  "name": "Endo TLIF (Endoscopic Transforaminal Lumbar Interbody Fusion)",
  "alternateName": "Endo TLIF",
  "description": "Endo TLIF is the endoscopic version of transforaminal lumbar interbody fusion, performed through an incision typically under one centimetre using continuous direct camera visualization, suited to well-selected straightforward cases.",
  "bodyLocation": "Lumbar spine",
  "performedBy": { "@type": "Physician", "name": "Dr. Amit Kumar Chanduka", "medicalSpecialty": "https://schema.org/Neurosurgery" },
  "areaServed": { "@type": "City", "name": "Bhubaneswar" }
};

const REASONS = [
  "Single-level disc disease or spondylolisthesis without significant deformity",
  "Patients keen to minimise hospital stay, blood loss, and post-operative discomfort",
  "Cases where the anatomy is straightforward enough for confident endoscopic access",
  "Patients wanting to return to light activity and desk-based work as quickly as safely possible",
];
const STEPS = [
  "You lie face-down on the operating table.",
  "Using X-ray guidance, Dr. Chanduka defines two entry points for different ports to the spine.",
  "One port is used for an endoscope camera (as in laparoscopic surgery), the other as a working port for instruments.",
  "The entire procedure is performed while watching a high-definition video feed from the endoscope.",
  "The damaged disc is removed and the compressed nerve is decompressed under continuous direct visualization.",
  "A small spacer packed with bone graft is placed to restore disc height and support fusion.",
  "Screws are placed through additional tiny incisions, guided by X-ray, to stabilise the segment.",
];
const BEFORE = [
  "Blood tests, and an ECG or chest X-ray if advised, to confirm you are fit for anaesthesia",
  "Some regular medicines (especially blood thinners) may need to be paused — you will get exact instructions",
  "You will be asked to stop eating and drinking for a set number of hours before surgery",
  "Arrange for a family member to be with you, and for help at home for the first 2\u20133 weeks after you return",
];
const RECOVERY = [
  ["Day 0\u20132", "Many patients are walking with support within hours of surgery. Pain is typically mild and well controlled with medication. Hospital stay is often just 2\u20133 days."],
  ["Week 1\u20132", "Rest at home with regular short walks. Avoid bending, twisting, or lifting anything heavier than about 2\u20133 kg."],
  ["Week 3\u20134", "Walking distance increases steadily. Light household and desk-based activity often resumes during this window."],
  ["Month 2", "Back strengthening exercises typically begin to rebuild core and back strength."],
  ["Month 3\u201312", "Bone fusion continues to strengthen, checked with follow-up X-rays. Full activity is guided by how solid the fusion looks and how you feel."],
];
const REDFLAGS = [
  "Fever, or chills",
  "Redness, swelling or fluid leaking from the incision",
  "New numbness or weakness in your legs",
  "Loss of bladder or bowel control, or pain suddenly much worse than before",
];

const SectionTitle = ({ children }) => <h2 className="font-display font-light text-3xl md:text-4xl text-[#161C22]">{children}</h2>;
const BulletList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-[#161C22]/75 leading-relaxed">
        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#5ECCA0] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
const NumberedList = ({ items }) => (
  <ol className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4 text-[#161C22]/75 leading-relaxed">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#E3F0EA] text-[#1F3864] text-sm font-medium flex items-center justify-center font-mono-ui">{i + 1}</span>
        <span className="pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);
const Callout = ({ title, children }) => (
  <div className="rounded-xl border p-6 bg-[#E3F0EA] border-[#5ECCA0]/40">
    <div className="text-sm font-medium mb-1.5 text-[#1F3864]">{title}</div>
    <p className="text-sm leading-relaxed text-[#161C22]/70">{children}</p>
  </div>
);

export default function EndoTLIF() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Endo TLIF Surgery in Bhubaneswar — Dr. Amit Chanduka";
    return () => { document.title = prevTitle; };
  }, []);
  useEffect(() => {
    axios.get(`${API}/specialization-faqs`, { params: { slug: "endo-tlif" } }).then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#F3F5F4] min-h-screen" data-testid="endo-tlif-page">
      <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      <SpineRail />
      <Nav />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#1F3864] text-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <a href="/#specializations" className="inline-flex items-center gap-2 text-white/60 hover:text-[#5ECCA0] text-sm mb-8 transition-colors">
                <ArrowLeft size={16} /> Back to Specializations
              </a>
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-[#5ECCA0] mb-6">
                <Camera size={26} strokeWidth={1.5} />
              </div>
              <Eyebrow tone="onDark">Specialization</Eyebrow>
              <h1 className="font-display font-light text-5xl md:text-6xl mt-5 leading-tight">Endo TLIF</h1>
              <p className="text-white/70 mt-3 text-lg md:text-xl">Endoscopic Transforaminal Lumbar Interbody Fusion — a guide for patients and families</p>
              <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
                A guide by Dr. Amit Chanduka, Neurosurgeon &amp; Spine Surgeon, explaining Endo TLIF, who it suits,
                and what to expect before, during, and after surgery.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Is Endo TLIF?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Endo TLIF is the endoscopic version of Transforaminal Lumbar Interbody Fusion — a minimally invasive
                way of performing this surgery. Instead of a tubular retractor, the surgeon works through two holes,
                one for a camera for visualization and a second as a working channel for performing the procedure.
                The endoscopic approach can be done using saline (water) as the visualization medium, or with air.
              </p>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                The goal is identical to open or MIS TLIF — removing the damaged disc, decompressing the nerve, and
                fusing the two vertebrae with a spacer and screws — but it is done through an incision typically
                under one centimetre, with continuous direct camera visualization rather than the naked eye.
              </p>
              <div className="mt-6">
                <Callout title="The smallest possible opening">
                  If MIS TLIF is a narrow tunnel to the spine, Endo TLIF is a keyhole — the smallest possible opening
                  that still allows the full procedure to be carried out safely and thoroughly.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>When Is Endo TLIF Recommended?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Dr. Chanduka recommends Endo TLIF for indications similar to TLIF generally. It particularly suits
                patients who are good candidates for fusion surgery and want the least disruptive option available,
                typically:
              </p>
              <div className="mt-6"><BulletList items={REASONS} /></div>
              <div className="mt-6">
                <Callout title="Not every case is suitable">
                  If imaging shows complex anatomy, prior surgery at the same level, or significant deformity, Dr.
                  Chanduka will recommend MIS or open TLIF instead for safety.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Happens During the Procedure</SectionTitle>
              <div className="mt-6"><NumberedList items={STEPS} /></div>
              <div className="flex items-center gap-2.5 mt-8 text-[#161C22]/60 text-sm">
                <Clock size={16} className="text-[#5ECCA0]" />
                The surgery usually takes between two and three hours for a single level. Because blood loss and
                tissue disruption are minimal, some patients are suitable for a shorter hospital stay than with open
                TLIF.
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Preparing for Your Surgery</SectionTitle>
              <div className="mt-6"><BulletList items={BEFORE} /></div>
              <p className="text-[#161C22]/75 mt-6 leading-relaxed text-sm">
                Endo TLIF is done under general anaesthesia. While spinal, local or regional anaesthesia is
                advocated by a few surgeons, Dr. Chanduka typically does not recommend it, in view of patient safety.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Recovery: What to Expect</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                The endoscopic approach generally offers the fastest early recovery of the three TLIF techniques,
                since almost no muscle is disturbed.
              </p>
              <div className="mt-6 space-y-4">
                {RECOVERY.map(([stage, text]) => (
                  <div key={stage} className="bg-white rounded-xl border border-[#DCE3E0] p-5">
                    <div className="font-medium text-[#1F3864] text-sm mb-1">{stage}</div>
                    <p className="text-sm text-[#161C22]/70 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Risks and When to Call Us</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Endo TLIF carries the same fundamental risks as any spine fusion — infection, bleeding, nerve
                irritation, or delayed fusion — generally with less blood loss and muscle trauma than open
                approaches. Because it is a technically demanding technique, case selection matters: Dr. Chanduka
                will only recommend it when your specific anatomy is well suited to it.
              </p>
              <p className="text-[#161C22] font-medium mt-8 mb-4">Please contact the clinic promptly if, after surgery, you notice:</p>
              <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                <ul className="space-y-3">
                  {REDFLAGS.map((item, i) => (
                    <li key={i} className="flex gap-3 text-red-700/90 leading-relaxed text-sm">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {faqs.length > 0 && (
          <section className="py-16 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-8">
              <Reveal>
                <SectionTitle>Frequently Asked Questions</SectionTitle>
                <Accordion type="single" collapsible className="mt-8">
                  {faqs.map((item) => (
                    <AccordionItem key={item.id} value={item.id} className="border-[#DCE3E0]">
                      <AccordionTrigger className="text-left font-display text-lg text-[#161C22] hover:no-underline">{item.question}</AccordionTrigger>
                      <AccordionContent className="text-[#161C22]/70 leading-relaxed">{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </section>
        )}

        <section className="py-16 md:py-20 bg-[#1F3864] text-white text-center">
          <div className="max-w-[700px] mx-auto px-5 md:px-8">
            <Reveal>
              <CheckCircle2 size={28} className="text-[#5ECCA0] mx-auto mb-5" />
              <h2 className="font-display font-light text-3xl md:text-4xl">Have questions about Endo TLIF?</h2>
              <p className="text-white/70 mt-4 leading-relaxed">
                This page is for general education and does not replace a personal consultation. Please discuss
                how this applies to your own case with Dr. Chanduka before making any treatment decision.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
